import { useCallback, useEffect, useState } from 'react';
import { FRIENDLY_DEPENDENCY_NAMES } from '../constants/dependency';
import { getAllDependents } from '../sources/dependents';
import translations from '../translations';
import type { DependencyAPI } from '../types/dependents';
import CopyableText from './generic/CopyableText';
import Table, { type TableColumnList } from './generic/Table';
import { useNotificationDispatch } from './notifications/NotificationProvider';

type DependentsProps = {
    id: string;
};

type Dependent = {
    type: (typeof FRIENDLY_DEPENDENCY_NAMES)[keyof typeof FRIENDLY_DEPENDENCY_NAMES];
} & Omit<DependencyAPI, 'type'>;

const Dependents = ({ id }: DependentsProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [dependents, setDependents] = useState<Dependent[]>([]);
    const [paging, setPaging] = useState({
        total: 0,
        pageSize: 10,
        page: 1,
    });

    const dispatchNotification = useNotificationDispatch();

    const fetchDependents = useCallback(
        async (page: number) => {
            try {
                setIsLoading(true);

                const filters = {
                    pageSize: paging.pageSize,
                    page,
                };

                const results = await getAllDependents({
                    id,
                    filters,
                });

                const newDependents = results.items.map((dependent) => ({
                    ...dependent,
                    type: FRIENDLY_DEPENDENCY_NAMES[dependent.type],
                }));

                setDependents(newDependents);

                setPaging(results._meta);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setIsLoading(false);
            }
        },
        [id, paging.pageSize, dispatchNotification],
    );

    const handlePageChange = (page: number) => {
        setPaging({ ...paging, page });
        fetchDependents(page);
    };

    useEffect(() => {
        fetchDependents(1);
    }, [fetchDependents]);

    const dependantColumns: TableColumnList<Dependent> = [
        {
            renderHeader: () => translations.COMMON_TABLE_name,
            renderCell: ({ item }) => item.developerName,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_type,
            renderCell: ({ item }) => item.type,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_id,
            renderCell: ({ item }) => <CopyableText copyableText={item.id} hasCopyButton={true} />,
        },
    ];

    return (
        <>
            <h2>{translations.DEPENDENTS_table_title}</h2>
            <Table
                wrapperClassName="margin-top"
                columns={dependantColumns}
                items={dependents}
                pagination={{
                    ...paging,
                    changePage: handlePageChange,
                }}
                isLoading={isLoading}
            />
        </>
    );
};

export default Dependents;
